import API from '@api/API';
import type {
  AttachEntitiesDto,
  CreatePresetDto,
  CreateStepDto,
  DisconnectPresetDto,
  GetStatusResponse,
  GetTasksResponse,
  PreviewPresetDto,
  PreviewPresetResponse,
  PullPresetDto,
  RenamePresetDto,
  ReorderStepsDto,
  SetPresetDto,
  SetStatusDto,
  UpdatePresetDto,
  UpdateStepDto,
  UpdateSubtaskCategoryDto,
} from '@/api';
import { ENDPOINTS } from '@/api/endpoints';
import type { Backlink } from '@api/types/backlinks';

export const statusApi = {
  // Get all status groups and steps for an audit
  getAll: async (auditId: string): Promise<GetStatusResponse> => {
    const res = await API().get(ENDPOINTS.status.getAll(auditId));
    return res.data;
  },

  // Get all tasks (steps) for an audit
  getTasks: async (auditId: string): Promise<GetTasksResponse> => {
    const res = await API().get(ENDPOINTS.status.getTasks(auditId));
    return res.data;
  },

  // Preview a preset before applying it
  previewPreset: async (
    auditId: string,
    payload: PreviewPresetDto,
  ): Promise<PreviewPresetResponse> => {
    const res = await API().post(ENDPOINTS.status.previewPreset(auditId), payload);
    return res.data;
  },

  // Start editing mode for status
  startEdit: async (auditId: string): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.startEdit(auditId));
    return res.data;
  },

  // Save current status changes
  save: async (auditId: string): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.save(auditId));
    return res.data;
  },

  // Reorder steps
  reorderSteps: async (auditId: string, payload: ReorderStepsDto): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.reorderSteps(auditId), payload);
    return res.data;
  },

  // Update preset
  updatePreset: async (auditId: string, payload: UpdatePresetDto): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.updatePreset(auditId), payload);
    return res.data;
  },

  // Sync update (deploy preset changes)
  syncUpdate: async (auditId: string, payload: UpdatePresetDto): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.syncUpdate(auditId), payload);
    return res.data;
  },

  // Pull preset updates
  pullPreset: async (auditId: string, payload: PullPresetDto): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.pullPreset(auditId), payload);
    return res.data;
  },

  // Disconnect from preset
  disconnect: async (auditId: string, payload: DisconnectPresetDto): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.disconnect(auditId), payload);
    return res.data;
  },

  // Create a new step
  createStep: async (auditId: string, payload: CreateStepDto): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.createStep(auditId), payload);
    return res.data;
  },

  // Update a step
  updateStep: async (
    auditId: string,
    stepId: string | number,
    payload: UpdateStepDto,
  ): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.updateStep(auditId, stepId), payload);
    return res.data;
  },

  // Attach entities (documents, meetings, requests) to a step
  attachEntity: async (
    auditId: string,
    stepId: string | number,
    payload: AttachEntitiesDto,
  ): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.attachEntity(auditId, stepId), payload);
    return res.data;
  },

  /**
   * The places that cite this task (0.54.0). AUDITOR ROLE ONLY — fire it for auditors
   * alone. 400 `{ message: 'Invalid request data: stepId: …' }`; 404 is a BARE STRING
   * `"Status task not found"`.
   */
  getStepBacklinks: async (
    auditId: string,
    stepId: string | number,
  ): Promise<{ backlinks: Backlink[] }> => {
    const res = await API().get(ENDPOINTS.status.stepBacklinks(auditId, stepId));
    return res.data;
  },

  // Delete a step
  deleteStep: async (auditId: string, stepId: string | number): Promise<void> => {
    const res = await API().delete(ENDPOINTS.status.deleteStep(auditId, stepId));
    return res.data;
  },

  // Mark a step as done
  markAsDone: async (auditId: string, stepId: string | number): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.markAsDone(auditId, stepId));
    return res.data;
  },

  // Set step status
  setStatus: async (
    auditId: string,
    stepId: string | number,
    payload: SetStatusDto,
  ): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.setStatus(auditId, stepId), payload);
    return res.data;
  },

  // Toggle step status
  toggleStatus: async (auditId: string, stepId: string | number): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.toggleStatus(auditId, stepId));
    return res.data;
  },

  // Update subtask category
  updateSubtaskCategory: async (
    auditId: string,
    stepId: string | number,
    payload: UpdateSubtaskCategoryDto,
  ): Promise<void> => {
    const res = await API().patch(ENDPOINTS.status.updateSubtaskCategory(auditId, stepId), payload);
    return res.data;
  },

  // Create new preset
  newPreset: async (auditId: string, payload: CreatePresetDto): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.newPreset(auditId), payload);
    return res.data;
  },

  // Set/apply preset to audit
  setPreset: async (auditId: string, payload: SetPresetDto): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.setPreset(auditId), payload);
    return res.data;
  },

  // Rename preset
  renamePreset: async (
    auditId: string,
    presetId: string | number,
    payload: RenamePresetDto,
  ): Promise<void> => {
    const res = await API().post(ENDPOINTS.status.renamePreset(auditId, presetId), payload);
    return res.data;
  },

  // Delete preset
  deletePreset: async (auditId: string, presetId: string | number): Promise<void> => {
    const res = await API().delete(ENDPOINTS.status.deletePreset(auditId, presetId));
    return res.data;
  },
};
